import React, { FC, useContext } from 'react';
import { ContextApi } from 'react-uforms';
import { useLoadScript, GoogleMap } from '@react-google-maps/api';
import { Question } from '@ch/common/types/data';
import { FormInput } from '@ch/common/components/form/input';
import { addressPopulationObject } from './const';
import { QuizContext } from './quiz-context';
import { loadFile } from './helpers/file';
import { useLang } from '../use-lang';
interface AutocompleteInputProps extends Question {
  name: string;
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
}

const libraries = ['places' as googleLibraries];
type googleLibraries = 'places' | 'drawing' | 'geometry' | 'localContext' | 'visualization';

const QuizFieldAutocompleteInput: FC<AutocompleteInputProps> = ({ name, placeholder, disabled, onPlaceSelected }) => {
  const { lang } = useLang();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_API_KEY}`,
    language: lang,
    libraries,
  });
  const { getErrors } = useContext(ContextApi);
  const { userQuestions } = useContext(QuizContext);

  const handleLoad = () => {
    const autoCompleteInput = document.getElementById(name) as HTMLInputElement;
    const autocomplete = new google.maps.places.Autocomplete(autoCompleteInput);
    google.maps.event.addListener(autocomplete, 'place_changed', async () => {
      const place = autocomplete.getPlace();
      let optionsData = [];
      if (place) {
        const addressComponents = place.address_components;
        const formattedAddress = addressPopulationObject[name];
        if (formattedAddress.country) {
          const countryQuestion = userQuestions.find(question => question.identifier === formattedAddress.country);
          const countryDropdownFile = countryQuestion.file;
          optionsData = await loadFile(countryDropdownFile);
        }
        const responseAddress = {};
        addressComponents.forEach(a => {
          const name = a.long_name;
          const type = a.types[0];
          switch (type) {
            case 'locality':
              if (formattedAddress.city) {
                responseAddress[formattedAddress.city] = name;
              }
              break;

            case 'administrative_area_level_1':
              if (formattedAddress.state) {
                responseAddress[formattedAddress.state] = name;
              }
              break;

            case 'country':
              if (formattedAddress.country) {
                const selectedCountry = optionsData.find(country => country.e2b_code === a.short_name);
                responseAddress[formattedAddress.country] = selectedCountry.value;
              }
              break;

            case 'postal_code':
              if (formattedAddress.postalCode) {
                responseAddress[formattedAddress.postalCode] = name;
              }
              break;
          }
        });
        const response = {
          ...place,
          streetAddress: place.formatted_address,
          responseAddress,
        };
        onPlaceSelected(response);
      }
    });
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps</div>;

  return (
    <div>
      <GoogleMap onLoad={handleLoad} />
      <FormInput
        {...{
          id: name,
          name,
          placeholder,
          $error: !!getErrors(name)?.length,
          disabled,
        }}
      />
    </div>
  );
};

export default QuizFieldAutocompleteInput;
